import { clearAccountInfoCache, fetchAccountInfo } from './accountInfo';
import { sendLowCreditsNotification } from './notifications';
import { fetchSiteSettings } from './siteSettings';
import { getVideoGenerationCreditCost } from './videoDuration';
import {
  fetchAwsApi,
  getApiErrorMessage,
  getAuthenticatedUser,
  parseApiResponse,
  storeAuthenticatedUser,
} from '../security';

type UnknownRecord = Record<string, unknown>;
type GenerationType = 'image' | 'video';

interface ChargeAccountCreditsOptions {
  units?: number;
  /**
   * Chosen video length in seconds. Video credit cost is
   * `pricePerVideoSecond` × `durationSeconds`. Ignored for image generation.
   */
  durationSeconds?: number;
}

let creditsUpdateQueue: Promise<void> = Promise.resolve();

const parsedCreditsAlertThreshold = Number(import.meta.env.VITE_CREDIT_ALERT_THRESHOLD);

export const CREDITS_ALERT_THRESHOLD = Number.isFinite(parsedCreditsAlertThreshold) && parsedCreditsAlertThreshold >= 0
  ? Math.trunc(parsedCreditsAlertThreshold)
  : 20;
export interface GenerationCreditsEligibility {
  hasSufficientCredits: boolean;
  currentCredits: number | null;
  requiredCredits: number;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeCredits(value: unknown) {
  const parsedValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return Math.max(0, Math.trunc(parsedValue));
}

export function extractAccountCredits(value: unknown): number | null {
  if (!isRecord(value)) {
    return null;
  }

  const directCredits = normalizeCredits(
    value.credits ??
    value.credit ??
    value.available_credits ??
    value.availableCredits
  );
  if (directCredits !== null) {
    return directCredits;
  }

  const nestedCandidates: unknown[] = [
    value.account,
    value.user,
    value.profile,
    value.data,
    value.result,
  ];

  for (const candidate of nestedCandidates) {
    const nestedCredits = extractAccountCredits(candidate);
    if (nestedCredits !== null) {
      return nestedCredits;
    }
  }

  return null;
}

function applyCreditsToRecord(value: unknown, credits: number): unknown {
  if (!isRecord(value)) {
    return value;
  }

  const nextRecord: UnknownRecord = {
    ...value,
    credits,
  };

  if ('credit' in value) {
    nextRecord.credit = credits;
  }
  if ('available_credits' in value) {
    nextRecord.available_credits = credits;
  }
  if ('availableCredits' in value) {
    nextRecord.availableCredits = credits;
  }
  if (isRecord(value.account)) {
    nextRecord.account = applyCreditsToRecord(value.account, credits);
  }
  if (isRecord(value.user)) {
    nextRecord.user = applyCreditsToRecord(value.user, credits);
  }
  if (isRecord(value.profile)) {
    nextRecord.profile = applyCreditsToRecord(value.profile, credits);
  }

  return nextRecord;
}

export function getStoredAccountCredits() {
  return extractAccountCredits(getAuthenticatedUser());
}

export function storeAccountCredits(credits: number) {
  const authenticatedUser = getAuthenticatedUser();
  if (!isRecord(authenticatedUser)) {
    return;
  }

  const normalizedCredits = normalizeCredits(credits);
  if (normalizedCredits === null) {
    return;
  }

  const updatedAuthenticatedUser = applyCreditsToRecord(authenticatedUser, normalizedCredits);
  if (JSON.stringify(updatedAuthenticatedUser) === JSON.stringify(authenticatedUser)) {
    return;
  }

  storeAuthenticatedUser(updatedAuthenticatedUser);
}

export async function refreshAccountCredits() {
  clearAccountInfoCache();
  const remoteAccountInfo = await fetchAccountInfo();
  const remoteCredits = extractAccountCredits(remoteAccountInfo);
  if (remoteCredits !== null) {
    storeAccountCredits(remoteCredits);
    return remoteCredits;
  }

  return getStoredAccountCredits();
}

function didCrossLowCreditsThreshold(previousCredits: number | null, nextCredits: number | null) {
  if (previousCredits === null || nextCredits === null) {
    return false;
  }

  return previousCredits >= CREDITS_ALERT_THRESHOLD && nextCredits < CREDITS_ALERT_THRESHOLD;
}

async function chargeAccountCreditsForGenerationInternal(
  generationType: GenerationType,
  options: ChargeAccountCreditsOptions = {}
) {
  const siteSettings = await fetchSiteSettings();
  let generationPrice: number;

  if (generationType === 'video') {
    generationPrice = getVideoGenerationCreditCost(
      siteSettings.pricePerVideoSecond,
      options.durationSeconds
    );
  } else if (generationType === 'image') {
    // Backend charges 1 unit per image generation. Charge only the additional
    // units here when a multiplier applies (e.g. new category = 3×).
    const extraUnits = Math.max(0, (options.units ?? 1) - 1);
    generationPrice = extraUnits * (siteSettings.pricePerImage ?? 0);
  } else {
    return;
  }

  if (generationPrice <= 0) {
    return;
  }

  const currentCredits = await refreshAccountCredits();

  const response = await fetchAwsApi('/api/update_account', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      credits: generationPrice,
    }),
  });
  const payload = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(payload, 'Unable to update account credits.'));
  }

  clearAccountInfoCache();

  const returnedCredits = extractAccountCredits(payload);
  if (returnedCredits !== null) {
    storeAccountCredits(returnedCredits);
    if (didCrossLowCreditsThreshold(currentCredits, returnedCredits)) {
      void sendLowCreditsNotification().catch(() => {
        // Notification failures should not block successful credit updates.
      });
    }
    return;
  }

  if (currentCredits !== null) {
    const nextCredits = Math.max(0, currentCredits - generationPrice);
    storeAccountCredits(nextCredits);
    if (didCrossLowCreditsThreshold(currentCredits, nextCredits)) {
      void sendLowCreditsNotification().catch(() => {
        // Notification failures should not block successful credit updates.
      });
    }
  }
}

export async function getGenerationCreditsEligibility(
  generationType: GenerationType | 'avatar',
  options: ChargeAccountCreditsOptions = {}
): Promise<GenerationCreditsEligibility> {
  const currentCredits = await refreshAccountCredits();
  let requiredCredits = 0;

  try {
    const siteSettings = await fetchSiteSettings();
    if (generationType === 'image' || generationType === 'avatar') {
      const rawRequiredCredits = generationType === 'avatar' ? siteSettings.pricePerAvatar : siteSettings.pricePerImage;
      requiredCredits = Number.isFinite(rawRequiredCredits) && rawRequiredCredits > 0
        ? Math.trunc(rawRequiredCredits)
        : 0;
    } else {
      requiredCredits = getVideoGenerationCreditCost(
        siteSettings.pricePerVideoSecond,
        options.durationSeconds
      );
    }
  } catch {
    requiredCredits = 0;
  }

  if (requiredCredits <= 0 || currentCredits === null) {
    return {
      hasSufficientCredits: true,
      currentCredits,
      requiredCredits,
    };
  }

  return {
    hasSufficientCredits: currentCredits >= requiredCredits,
    currentCredits,
    requiredCredits,
  };
}

export function chargeAccountCreditsForGeneration(
  generationType: GenerationType,
  options: ChargeAccountCreditsOptions = {}
) {
  creditsUpdateQueue = creditsUpdateQueue
    .catch(() => {
      // Keep queue alive after previous failures.
    })
    .then(() => chargeAccountCreditsForGenerationInternal(generationType, options));

  return creditsUpdateQueue;
}
